/**
 * A cookie written OUTSIDE React needs a way to reach the components reading it.
 *
 * `useCookie` seeds its state from `document.cookie` once, at mount, and updates it
 * only through its own setter. That is fine for every write React makes, but the
 * axios request interceptor also writes the auth-token cookie when it recovers a
 * missing or expired one, and it does so with a bare `setCookie` — no React
 * involved. The state therefore stayed `null`, and anything gated on it stayed
 * switched off: `useRbac` (`enabled: !!currentUser && !!authTokenCookie`) never
 * fetched roles, so a recovered session rendered "Access restricted" instead of
 * the app.
 *
 * There is no browser event for cookie changes, so this is the smallest bridge
 * that closes the gap: the non-React writer announces the name it just wrote, and
 * `useCookie` re-reads that one cookie.
 */
export const COOKIE_CHANGED_EVENT = "saras:cookie-changed";

export interface CookieChangedDetail {
  name: string;
}

/** Announce that `name` was written outside React, so `useCookie` can catch up. */
export const notifyCookieChanged = (name: string) => {
  // Guarded for non-DOM environments (SSR, plain node test runners).
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<CookieChangedDetail>(COOKIE_CHANGED_EVENT, {
      detail: { name },
    })
  );
};
